import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatNumber } from "@/lib/format";
import { useLicenseLabels } from "@/hooks/useLicenseLabels";
import type { SoftwareProduct } from "@/pages/products/hooks/useSoftwareProducts";

interface ProductCardProps {
  product: SoftwareProduct;
  onOpenDetails: (product: SoftwareProduct) => void;
}

export default function ProductCard({ product, onOpenDetails }: ProductCardProps) {
  const { t } = useTranslation();
  const { edition } = useLicenseLabels();
  const editions = product.editions ?? [];
  const versionCount = product.versions?.length ?? 0;

  return (
    <article className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300">
      <div className="flex items-start justify-between gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
          <i className={`${product.icon} text-xl`} />
        </span>
        {product.category && (
          <span className="rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-medium text-secondary-900">
            {product.category}
          </span>
        )}
      </div>

      <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{product.name}</h3>
      <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{product.summary}</p>

      <dl className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-600">
        <div className="flex items-center gap-1.5">
          <span className="flex h-4 w-4 items-center justify-center text-accent-500">
            <i className="ri-star-fill" />
          </span>
          <span className="font-medium text-foreground-950">{Number(product.rating).toFixed(1)}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-download-2-line" />
          </span>
          {t("products.downloadsUnit", { count: formatNumber(product.downloads) })}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-git-branch-line" />
          </span>
          {t("products.versionsUnit", { count: versionCount })}
        </div>
      </dl>

      <div className="mt-4 flex flex-wrap gap-1.5">
        {editions.map((item) => (
          <span
            key={item.key}
            className="rounded-md bg-background-100 px-2 py-0.5 text-[11px] text-foreground-700"
          >
            {edition(item.key)}
          </span>
        ))}
      </div>

      <div className="mt-5 flex items-center justify-between gap-2 border-t border-background-200 pt-4">
        <span className="flex items-center gap-1.5 text-xs text-foreground-500">
          <span className="flex h-4 w-4 items-center justify-center text-primary-600">
            <i className="ri-price-tag-3-line" />
          </span>
          {t("products.latest", { version: product.latest_version })}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onOpenDetails(product)}
            className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100"
          >
            {t("products.versionDetail")}
          </button>
          <Link
            to={`/apply?product=${product.slug}`}
            className="whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            {t("products.apply")}
          </Link>
        </div>
      </div>
    </article>
  );
}